import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Component, inject, OnInit } from '@angular/core';
import { Currency } from '@app/finxone-web-frontend/app/lib/dtos/currencies.dto';
import { SafeHtmlPipe } from '@app/finxone-web-frontend/app/lib/pipes/SafeHtml.pipe';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { AccountState } from '@app/finxone-web-frontend/app/lib/state/account.state';
import {
  countryCodeToCircleFlag,
  currencyToCircleFlag,
} from '@app/finxone-web-frontend/app/lib/utils/template/handlebar-helpers/currency-to-flag.utils';
import {
  removeDuplicateCurrencies,
  removeInvalidCurrencies,
} from '@app/finxone-web-frontend/app/lib/utils/utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  AllowedCountryType,
  BaseWidgetProperties,
  DropdownMenuDirection,
  FormFieldConfig,
  ProviderClass,
  WidgetLayoutAttributes,
} from '@finxone-platform/shared/sys-config-types';
import { FinxoneSelectDropdownComponent } from '@finxone-platform/ui-components';
import { Store } from '@ngxs/store';
import { Subject, map, switchMap, takeUntil } from 'rxjs';
import { BeneficiaryFormService } from '../beneficiary-form.service';
import { PARENT_FORM_ROW_GAP } from '@app/finxone-web-frontend/app/lib/utils/di-tokens';

@Component({
  selector: 'finxone-web-frontend-beneficiary-country-currency-widget',
  templateUrl: './beneficiary-country-currency-widget.component.html',
  imports: [NgStyle, NgTemplateOutlet, ReactiveFormsModule, FinxoneSelectDropdownComponent, SafeHtmlPipe],
})
export class BeneficiaryCountryCurrencyWidgetComponent implements OnInit {
  @AutoDestroy private destroy$ = new Subject<void>();

  public widgetProperties: BaseWidgetProperties;
  public beneficiaryForm: FormGroup;

  public readonly countryFormControlName = 'country';
  public readonly currencyFormControlName = 'currency';

  public readonly widgetInputFieldValidation: FormFieldConfig['validation'] = { required: true };

  public countryList: AllowedCountryType[] = [];
  public currencyCodes: { title: string; currencyFlag: string; value: string }[] = [];

  public selectedCountryOption: AllowedCountryType;
  public selectedCurrencyOption: { title: string; currencyFlag: string; value: string };
  public isScrollable = true;
  public isScrollPeekEnabled = false;
  public visibleItemsCount = 5;
  public menuDirection = DropdownMenuDirection.AUTO;

  public parentFormWidgetRowGap: string | undefined | null = inject(PARENT_FORM_ROW_GAP, { optional: true });

  constructor(
    private dataIn: ProviderClass<WidgetLayoutAttributes>,
    private configService: ConfigService,
    private store: Store,
    public beneficiaryFormService: BeneficiaryFormService,
  ) {}

  public ngOnInit(): void {
    this.loadCountryList();
    this.loadCurrencyList();
    this.initializeWidgetConfig();
  }

  private initializeWidgetConfig(): void {
    const widgetProperties = this.dataIn?.attri?.widgetProperties;
    if (!widgetProperties) return;

    this.widgetProperties = widgetProperties;
    this.beneficiaryForm = this.beneficiaryFormService.beneficiaryForm;

    const currentAccount = this.store.selectSnapshot(AccountState.getCurrentAccount);
    const countryValue = this.beneficiaryForm.get(this.countryFormControlName)?.value;
    const currencyValue = this.beneficiaryForm.get(this.currencyFormControlName)?.value;

    const useDefaultCountry = !widgetProperties['isCountryVisible'];
    const useDefaultCurrency = !widgetProperties['isCurrencyVisible'];
    const defaultCurrency =
      widgetProperties['defaultBeneficiaryCurrency'] === 'selected_account_context'
        ? currentAccount?.currency
        : widgetProperties['defaultBeneficiaryCurrency'];

    const countryKey =
      useDefaultCountry && !countryValue ? widgetProperties['defaultBeneficiaryCountry'] : countryValue;
    const currencyKey = useDefaultCurrency && !currencyValue ? defaultCurrency : currencyValue;

    this.selectedCountryOption = this.findCountryByKey(countryKey) ?? this.findCountryByKey('GB');
    this.selectedCurrencyOption = this.findCurrencyByValue(currencyKey) ??
      this.currencyCodes[0] ?? {
        title: 'GBP',
        value: 'GBP',
        currencyFlag: currencyToCircleFlag('GBP', '24px'),
      };

    this.setFormControlValue(this.countryFormControlName, this.selectedCountryOption?.key);
    this.setFormControlValue(this.currencyFormControlName, this.selectedCurrencyOption?.value);
    this.isScrollable = this.dataIn.attri.widgetProperties?.inputFieldConfig?.isScrollable ?? true;
    this.isScrollPeekEnabled =
      this.dataIn.attri.widgetProperties?.inputFieldConfig?.isScrollPeekEnabled ?? false;
    this.visibleItemsCount = this.dataIn.attri.widgetProperties?.inputFieldConfig?.visibleItemsCount ?? 5;
    this.menuDirection =
      this.dataIn.attri.widgetProperties?.inputFieldConfig?.menuDirection ?? DropdownMenuDirection.AUTO;
  }

  private loadCountryList(): void {
    this.configService
      .getCountries()
      .pipe(
        takeUntil(this.destroy$),
        map(
          (countries) =>
            countries?.map(({ name, code }) => ({
              title: name,
              key: code,
              currencyFlag: countryCodeToCircleFlag(code, '24px'),
            })) ?? [],
        ),
      )
      .subscribe((list) => {
        this.countryList = list;
      });
  }

  private loadCurrencyList(): void {
    this.store
      .selectOnce(AccountState.getPaymentGatewayConfig)
      .pipe(
        map((config) => config.supportedCurrency ?? ['GBP', 'USD', 'EUR']),
        switchMap((supportedCurrencies) =>
          this.configService.getCurrencies().pipe(
            takeUntil(this.destroy$),
            map(this.filterAndTransformCurrencies),
            map((currencies) => currencies.filter((cur) => supportedCurrencies.includes(cur.value))),
          ),
        ),
      )
      .subscribe((codes) => {
        this.currencyCodes = codes;
      });
  }

  private filterAndTransformCurrencies = (
    response: Currency[],
  ): { title: string; value: string; currencyFlag: string }[] =>
    removeDuplicateCurrencies(removeInvalidCurrencies(response), 'currency_code').map((cur) => ({
      title: cur.currency_code,
      value: cur.currency_code,
      currencyFlag: currencyToCircleFlag(cur.currency_code, '24px'),
    }));

  private findCountryByKey(key: string) {
    return this.countryList.find((country) => country.key === key) as AllowedCountryType;
  }

  private findCurrencyByValue(value: string) {
    return this.currencyCodes.find((currency) => currency.value === value) as {
      title: string;
      value: string;
      currencyFlag: string;
    };
  }

  private setFormControlValue(controlName: string, value: string): void {
    this.beneficiaryForm.get(controlName)?.setValue(value);
  }

  public onSelectionChange(): void {
    const countryKey = this.beneficiaryForm.get(this.countryFormControlName)?.value;
    const currencyValue = this.beneficiaryForm.get(this.currencyFormControlName)?.value;

    this.selectedCountryOption = this.findCountryByKey(countryKey);
    this.selectedCurrencyOption = this.findCurrencyByValue(currencyValue);
  }
}
